import React from 'react'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import MuiFilterSelect from '../../components/Filters/MuiFilterSelect'
import { MUI_DATE_PICKER_ES_LOCALE_TEXT } from '../../components/constants/muiDatePickerEsLocaleText'
import { MobileAdvancedFilterDrawer } from '../../components/mobile'
import {
  MOBILE_ADVANCED_FILTER_DATE_CARD_SX,
  MOBILE_ADVANCED_FILTER_DATE_PICKER_TEXT_FIELD_SX,
  MOBILE_ADVANCED_FILTER_FIELD_SX
} from '../../components/mobile/styles/mobileAdvancedFilterSx'
import dayjs from '../../utils/dayjsConfig'
import {
  buildAppliedDateRange,
  buildSelectValue,
  formatDateRangeSummary
} from '../helpers/orderMobileFilters'

const mobileDatePickerSlotProps = {
  textField: {
    size: 'small',
    fullWidth: true,
    sx: MOBILE_ADVANCED_FILTER_DATE_PICKER_TEXT_FIELD_SX
  },
  openPickerButton: {
    sx: {
      color: '#597FA9'
    }
  }
}

const OrderMobileDateRangeCard = ({
  range,
  anyFilterEmpty,
  onDateChange,
  onClearDateRange
}) => {
  const dateSummary = formatDateRangeSummary(range)
  const hasRange = Boolean(buildAppliedDateRange(range).start)

  return (
    <Box sx={MOBILE_ADVANCED_FILTER_DATE_CARD_SX}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#F3F6F9',
                color: '#6E879C',
                flexShrink: 0
              }}
            >
              <CalendarMonthOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#223042' }}>
                Rango de fechas
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                {dateSummary}
              </Typography>
            </Box>
          </Stack>

          {hasRange ? (
            <Button
              variant="text"
              size="small"
              onClick={onClearDateRange}
              sx={{
                minWidth: 'auto',
                px: 1,
                color: '#597FA9',
                fontWeight: 700,
                borderRadius: 999
              }}
            >
              Limpiar
            </Button>
          ) : null}
        </Stack>

        <Stack spacing={1.25}>
          <MuiDatePicker
            label="Desde"
            value={range.start}
            onChange={(nextValue) => onDateChange('start', nextValue)}
            maxDate={range.end || dayjs().local().endOf('day')}
            disabled={anyFilterEmpty}
            format="DD/MM/YYYY"
            slotProps={mobileDatePickerSlotProps}
          />
          <MuiDatePicker
            label="Hasta"
            value={range.end}
            onChange={(nextValue) => onDateChange('end', nextValue)}
            minDate={range.start || undefined}
            maxDate={dayjs().local().endOf('day')}
            disabled={anyFilterEmpty}
            format="DD/MM/YYYY"
            slotProps={mobileDatePickerSlotProps}
          />
        </Stack>
      </Stack>
    </Box>
  )
}

const OrderIndexMobileFilters = ({
  query,
  handleSearchChange,
  handleSearchKeyDown,
  clearSearch,
  openFilters,
  appliedFilterGroupCount,
  appliedFilterChips,
  isOpen,
  closeFilters,
  mobileFilterConfigs,
  draftFilterSelections,
  handleDraftSelectChange,
  anyFilterEmpty,
  draftDateRange,
  handleDraftDateChange,
  clearDraftDateRange,
  clearAllFilters,
  applyFilters
}) => (
  <MobileAdvancedFilterDrawer
    title="Filtrar pedidos por"
    drawerTitle="Filtros de pedidos"
    query={query}
    onQueryChange={handleSearchChange}
    onQueryKeyDown={handleSearchKeyDown}
    onClearQuery={clearSearch}
    searchPlaceholder="Buscar por código o nombre..."
    isOpen={isOpen}
    onOpen={openFilters}
    onClose={closeFilters}
    appliedFilterGroupCount={appliedFilterGroupCount}
    appliedFilterChips={appliedFilterChips}
    onClearAll={clearAllFilters}
    onApply={applyFilters}
    filterFieldsContent={
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="es"
        localeText={MUI_DATE_PICKER_ES_LOCALE_TEXT}
      >
        <Grid container spacing={2}>
          {mobileFilterConfigs.map((filter) => (
            <Grid key={filter.key} size={{ xs: 12, sm: 6 }} sx={{ minWidth: 0 }}>
              <Box sx={MOBILE_ADVANCED_FILTER_FIELD_SX}>
                <MuiFilterSelect
                  title={filter.title}
                  options={filter.options}
                  value={draftFilterSelections[filter.key] || buildSelectValue()}
                  onChange={(nextValue) => handleDraftSelectChange(filter.key, nextValue)}
                  disabled={anyFilterEmpty || filter.options.length === 0}
                  width="100%"
                  matchTriggerWidth
                  inlineDropdown
                  autoFocusSearch={false}
                />
              </Box>
            </Grid>
          ))}

          <Grid size={{ xs: 12 }} sx={{ minWidth: 0 }}>
            <OrderMobileDateRangeCard
              range={draftDateRange}
              anyFilterEmpty={anyFilterEmpty}
              onDateChange={handleDraftDateChange}
              onClearDateRange={clearDraftDateRange}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
    }
  />
)

export default OrderIndexMobileFilters
